import { matterNoteTableColumns } from "@/components/features/matter/notes/matter-note-columns";
import { MatterNote } from "@/components/features/matter/notes/MatterNote";
import { MatterNoteComposer } from "@/components/features/matter/notes/MatterNoteComposer";
import {
  ActionEmptyState,
  Button,
  Input,
  LoadingSpinner,
  Text,
} from "@/components/ui";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/shadcn/Accordion/Accordion";
import { AccordionTitle } from "@/components/features/contact/AddOrEditContactModal/AccordionTitle";
import { FilterBar } from "@/components/ui/FilterBar/FilterBar";
import type { FilterOption } from "@/components/ui/FilterSelector/FilterSelector";
import { useUser } from "@/components/UserProvider";
import { useTableInfiniteScroll } from "@/hooks/useTableInfiniteScroll";
import { getUsersOptions } from "@/lib/data/react-query";
import { retrieveMassTortNotesInfiniteQueryOptions } from "@/lib/data/react-query/mass-torts/mass-tort-collections";
import { getMassTortAnnotationNotes } from "@/lib/utils/massTortAnnotationUtils";
import { retrieveMatterNotesV2 } from "@/lib/data/requests";
import { useMatterInfinitePages } from "@/hooks/use-matter-infinite-pages";
import {
  PAGE_PERMISSION_KEYS,
  ACTION_PERMISSION_KEYS,
  ACTION_PERMISSION_VALUES,
} from "@/lib/data/static/permissionOptions";
import {
  type Matter,
  type MatterNote as MatterNoteType,
  type MatterSubItemType,
} from "@/lib/types";
import { formatNotesWithType } from "@/lib/utils/noteUtils";
import {
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { AnimatePresence, motion } from "framer-motion";
import Fuse from "fuse.js";
import { isNil } from "lodash";
import { LockKeyhole } from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const getTimestampFromDate = (value?: string) => {
  if (!value) {
    return 0;
  }

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.valueOf() : 0;
};

const getNotePublishedDate = (note: MatterNoteType) => note.publishedDate;

export default function MatterNotes({
  matter,
  subItem,
  showPinnedOnly = false,
  pageSize = 100,
  disableBreadcrumbs = false,
  variant = "default",
  bareLayout = false,
  showAnimation = false,
  showComposerOverride = false,
  onComposerSuccess,
  defaultCreatePinned = false,
  showComments,
  commentsMode = "default",
  massTortId,
  onNoteCountChange,
}: {
  notes: MatterNoteType[];
  matter: Matter;
  subItem?: { id: string; type: MatterSubItemType };
  showPinnedOnly?: boolean;
  pageSize?: number;
  disableBreadcrumbs?: boolean;
  shrinkPagination?: boolean;
  variant?: "default" | "eventlog";
  bareLayout?: boolean;
  showAnimation?: boolean;
  showComposerOverride?: boolean;
  onComposerSuccess?: () => void;
  defaultCreatePinned?: boolean;
  showComments?: boolean;
  commentsMode?: "default" | "readOnly";
  massTortId?: string;
  onNoteCountChange?: (count: number | undefined) => void;
}) {
  const { hasPagePermission, hasActionPermission } = useUser();
  const { data: usersResponse } = useQuery(getUsersOptions());
  const hasViewPermission = hasPagePermission(
    massTortId
      ? PAGE_PERMISSION_KEYS.massTortsDetailNotes
      : PAGE_PERMISSION_KEYS.mattersDetailNotes,
  );
  const matterNotesQuery = useMatterInfinitePages({
    matterId: !massTortId && hasViewPermission ? matter.id : "",
    queryKey: "notes",
    queryFn: retrieveMatterNotesV2,
  });
  const massTortNotesQuery = useInfiniteQuery({
    ...retrieveMassTortNotesInfiniteQueryOptions({
      massTortId: massTortId ?? "",
    }),
    enabled: Boolean(massTortId) && hasViewPermission,
  });
  const notesQuery = massTortId ? massTortNotesQuery : matterNotesQuery;
  const {
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetching,
    isError,
    refetch,
    isLoading: isLoadingNotes,
  } = notesQuery;
  const notes = useMemo(
    () =>
      massTortId
        ? getMassTortAnnotationNotes(
            massTortNotesQuery.data?.pages.flatMap((page) => page.data) ?? [],
          )
        : (matterNotesQuery.data?.pages.flatMap((page) => page.data) ?? []),
    [massTortId, massTortNotesQuery.data?.pages, matterNotesQuery.data?.pages],
  );
  const noteCount = notesQuery.data?.pages[0]?.pagination?.count;
  useEffect(() => {
    onNoteCountChange?.(noteCount);
  }, [noteCount, onNoteCountChange]);
  const usersById = useMemo(
    () => new Map((usersResponse?.data ?? []).map((user) => [user.id, user])),
    [usersResponse],
  );
  const hasCreatePermission = hasActionPermission(
    massTortId
      ? ACTION_PERMISSION_KEYS.massTort
      : ACTION_PERMISSION_KEYS.matter,
    ACTION_PERMISSION_VALUES.update,
  );
  const [selectedFilters, setSelectedFilters] = useState<
    Partial<Record<string, string[]>>
  >({});
  // Local search state (frontend-only)
  const [query, setQuery] = useState("");
  const [selectedDraft, setSelectedDraft] = useState<MatterNoteType | null>(
    null,
  );

  const [discoverFilterOptions, setDiscoverFilterOptions] = useState(false);
  useEffect(() => {
    setDiscoverFilterOptions(false);
  }, [massTortId]);
  const hasActiveNoteFilters = Boolean(
    query.trim() ||
      showPinnedOnly ||
      subItem ||
      Object.values(selectedFilters).some((values) => values?.length),
  );
  const hasLocalFilters = discoverFilterOptions || hasActiveNoteFilters;
  useEffect(() => {
    if (
      massTortId &&
      hasLocalFilters &&
      hasNextPage &&
      !isFetching &&
      !isError
    ) {
      void fetchNextPage();
    }
  }, [
    massTortId,
    hasLocalFilters,
    hasNextPage,
    isFetching,
    isError,
    fetchNextPage,
    notes,
  ]);

  const handleAddFilter = (key: string, value: string) => {
    if (!value) {
      return;
    }

    setSelectedFilters((prev) => {
      const existingValues = prev[key] ?? [];
      const singleValueKeys = new Set([
        "pinned",
        "dateCreatedBefore",
        "dateCreatedAfter",
      ]);

      if (!singleValueKeys.has(key) && existingValues.includes(value)) {
        return prev;
      }

      return {
        ...prev,
        [key]: singleValueKeys.has(key) ? [value] : [...existingValues, value],
      };
    });
  };

  const handleRemoveFilter = (key: string, value: string) => {
    setSelectedFilters((prev) => {
      const existingValues = prev[key] ?? [];
      const nextValues = existingValues.filter((current) => current !== value);

      if (nextValues.length === 0) {
        const { [key]: removed, ...rest } = prev;
        void removed;
        return rest;
      }

      return {
        ...prev,
        [key]: nextValues,
      };
    });
  };

  const typedNotes = useMemo(
    () =>
      formatNotesWithType({ notes, matter }).filter(
        (note) => !subItem || note.itemId === subItem.id,
      ),
    [notes, matter, subItem],
  );

  const filterOptions = useMemo<FilterOption[]>(() => {
    const options: FilterOption[] = [];

    const userItems = Array.from(
      typedNotes
        .reduce((map, note) => {
          if (note.integrationAppId) {
            map.set(note.integrationAppId, {
              label: note.integrationAppName?.trim() || "Integration app",
              value: note.integrationAppId,
            });
            return map;
          }

          if (!note.userId) {
            return map;
          }

          const user = usersById.get(note.userId);
          if (!user || map.has(user.id)) {
            return map;
          }

          const firstName = (user.firstName || "").trim();
          const lastName = (user.lastName || "").trim();
          const label = `${firstName} ${lastName}`.trim().replace(/\s+/g, " ");
          if (!label) {
            return map;
          }

          map.set(user.id, {
            label,
            value: user.id,
          });

          return map;
        }, new Map<string, { label: string; value: string }>())
        .values(),
    ).sort((a, b) => a.label.localeCompare(b.label));

    if (userItems.length) {
      options.push({
        label: "User",
        value: "id",
        input: {
          type: "select",
          placeholder: "Select user",
          items: userItems,
        },
      });
    }

    const typeItems = Array.from(
      new Set(typedNotes.map((note) => note.type).filter(Boolean)),
    )
      .sort((a, b) => a.localeCompare(b))
      .map((type) => ({
        label: type,
        value: type,
      }));

    if (typeItems.length) {
      options.push({
        label: "Type",
        value: "type",
        input: {
          type: "select",
          placeholder: "Select type",
          items: typeItems,
        },
      });
    }

    options.push({
      label: "Created",
      value: "dateCreated",
      input: {
        type: "date",
        placeholder: "Select date",
        conditionOptions: [
          { label: "Before", value: "dateCreatedBefore" },
          { label: "After", value: "dateCreatedAfter" },
        ],
      },
    });

    if (!showPinnedOnly) {
      options.push({
        label: "Pinned",
        value: "pinned",
        input: {
          type: "select",
          placeholder: "Pinned state",
          items: [
            { label: "Pinned", value: "yes" },
            { label: "Not pinned", value: "no" },
          ],
        },
      });
    }

    return options;
  }, [showPinnedOnly, typedNotes, usersById]);

  const filteredBySelector = useMemo(() => {
    const userIds = selectedFilters.id ?? [];
    const typeFilters = selectedFilters.type ?? [];
    const pinnedValue = (selectedFilters.pinned ?? [])[0];
    const dateCreatedAfterValue = (selectedFilters.dateCreatedAfter ?? [])[0];
    const dateCreatedBeforeValue = (selectedFilters.dateCreatedBefore ?? [])[0];

    const createdAfterBoundary = dateCreatedAfterValue
      ? dayjs(dateCreatedAfterValue, "YYYY-MM-DD", true).startOf("day")
      : null;
    const createdBeforeBoundary = dateCreatedBeforeValue
      ? dayjs(dateCreatedBeforeValue, "YYYY-MM-DD", true).endOf("day")
      : null;

    return typedNotes.filter((note) => {
      if (note.draft) return false;

      if (showPinnedOnly && !note.pinned) {
        return false;
      }

      const authorId = note.integrationAppId ?? note.userId;
      if (userIds.length && (!authorId || !userIds.includes(authorId))) {
        return false;
      }

      if (typeFilters.length && !typeFilters.includes(note.type)) {
        return false;
      }

      if (pinnedValue === "yes" && !note.pinned) {
        return false;
      }

      if (pinnedValue === "no" && note.pinned) {
        return false;
      }

      const publishedDate = getNotePublishedDate(note);
      if (!publishedDate) {
        if (createdAfterBoundary || createdBeforeBoundary) {
          return false;
        }
        return true;
      }

      const createdDate = dayjs(publishedDate);
      if (!createdDate.isValid()) {
        return false;
      }

      if (createdAfterBoundary && createdDate.isBefore(createdAfterBoundary)) {
        return false;
      }

      if (createdBeforeBoundary && createdDate.isAfter(createdBeforeBoundary)) {
        return false;
      }

      return true;
    });
  }, [selectedFilters, showPinnedOnly, typedNotes]);

  const draftNotes = useMemo(
    () =>
      typedNotes
        .filter((note) => note.draft)
        .sort(
          (a, b) =>
            getTimestampFromDate(getNotePublishedDate(b)) -
            getTimestampFromDate(getNotePublishedDate(a)),
        ),
    [typedNotes],
  );

  const filteredNotes = useMemo(() => {
    const sanitizedQuery = query.trim();

    if (!sanitizedQuery) {
      return filteredBySelector;
    }

    const searchable = filteredBySelector.map((note) => ({
      ...note,
      author: (() => {
        if (note.integrationAppId) {
          return `${note.integrationAppName?.trim() || "Integration app"} ${note.integrationAppId}`;
        }
        if (!note.userId) {
          return "";
        }
        const user = usersById.get(note.userId);
        return `${user?.firstName ?? ""} ${user?.lastName ?? ""}`.trim();
      })(),
    }));

    const fuse = new Fuse(searchable, {
      keys: [
        { name: "message", weight: 0.8 },
        { name: "author", weight: 0.2 },
        { name: "firstName", weight: 0.1 },
        { name: "lastName", weight: 0.1 },
      ],
      threshold: 0.35,
      ignoreLocation: true,
      minMatchCharLength: 1,
      includeScore: false,
      useExtendedSearch: false,
    });

    return fuse.search(sanitizedQuery).map((result) => result.item);
  }, [filteredBySelector, query, usersById]);

  const pinnedNotes = useMemo(() => {
    const loadedNoteIds = new Set(notes.map((note) => note.id));
    // The collection API has no pinned filter. Keep older pins visible from the
    // detail response until their page loads, then prefer its nested comments.
    const olderPins =
      massTortId && !hasActiveNoteFilters
        ? formatNotesWithType({
            notes: (Array.isArray(matter.notes)
              ? (matter.notes as MatterNoteType[])
              : []
            ).filter(
              (note) =>
                note.pinned && !note.draft && !loadedNoteIds.has(note.id),
            ),
            matter,
          })
        : [];
    return [...filteredNotes.filter((note) => note.pinned), ...olderPins].sort(
      (a, b) =>
        getTimestampFromDate(getNotePublishedDate(b)) -
        getTimestampFromDate(getNotePublishedDate(a)),
    );
  }, [filteredNotes, notes, massTortId, hasActiveNoteFilters, matter]);

  const showPinnedHighlight = !showPinnedOnly && pinnedNotes.length > 0;
  const totalNotesCount = filteredNotes.length;

  const notesTable = useReactTable({
    data: useMemo(() => filteredNotes, [filteredNotes]),
    columns: matterNoteTableColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    sortingFns: {
      customDateTime: (a, b, columnId) => {
        if (isNil(a.getValue(columnId))) {
          return 1;
        }
        if (isNil(b.getValue(columnId))) {
          return -1;
        }
        return dayjs(a.getValue(columnId)).diff(dayjs(b.getValue(columnId)));
      },
    },
    initialState: {
      sorting: [{ id: "Date Created", desc: true }],
    },
  });

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  const [scrollElement, setScrollElement] = useState<HTMLDivElement | null>(
    null,
  );
  const attachScrollContainer = useCallback(
    (element: HTMLDivElement | null) => {
      scrollContainerRef.current = element;
      setScrollElement(element);
    },
    [],
  );
  const infiniteScrollSentinelRef = useRef<HTMLDivElement | null>(null);
  const { visibleRows, totalRows, hasMoreRows, sentinelRef, setItemsToRender } =
    useTableInfiniteScroll(notesTable, pageSize, {
      getScrollElement: () => scrollContainerRef.current,
    });
  const hasVisibleNotes = totalRows > 0 || draftNotes.length > 0;

  useEffect(() => {
    const container = scrollElement;
    if (!container) return;

    const handleScroll = () => {
      const { scrollTop, scrollHeight, clientHeight } = container;
      const distanceFromBottom = scrollHeight - (scrollTop + clientHeight);

      if (distanceFromBottom < 200 && hasMoreRows) {
        setItemsToRender((current) =>
          Math.min(current + pageSize, notesTable.getRowModel().rows.length),
        );
      }
      if (distanceFromBottom < 200 && hasNextPage && !isFetching && !isError) {
        void fetchNextPage();
      }
    };

    container.addEventListener("scroll", handleScroll);
    return () => {
      container.removeEventListener("scroll", handleScroll);
    };
  }, [
    fetchNextPage,
    hasMoreRows,
    hasNextPage,
    isFetching,
    isError,
    pageSize,
    setItemsToRender,
    notesTable,
    scrollElement,
  ]);

  useEffect(() => {
    const container = scrollElement;
    const sentinel = infiniteScrollSentinelRef.current;
    if (!container || !sentinel || !hasNextPage || isFetching || isError) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          void fetchNextPage();
        }
      },
      { root: null, rootMargin: "200px" },
    );
    observer.observe(sentinel);
    return () => {
      observer.disconnect();
    };
  }, [fetchNextPage, hasNextPage, isFetching, isError, notes, scrollElement]);

  useEffect(() => {
    if (!selectedDraft) {
      return;
    }

    const stillExists = draftNotes.some((note) => note.id === selectedDraft.id);

    if (!stillExists) {
      setSelectedDraft(null);
    }
  }, [draftNotes, selectedDraft]);

  return (
    <>
      {!hasViewPermission && (
        <div className="flex min-h-0 flex-1 items-center justify-center p-4">
          <ActionEmptyState
            className="max-w-xl"
            icon={<LockKeyhole className="h-5 w-5" />}
            title="Notes unavailable"
            description={`You do not have permission to view notes for this ${massTortId ? "mass tort" : "matter"}.`}
          />
        </div>
      )}

      {hasViewPermission && (
        <div
          className={
            bareLayout
              ? "flex h-full min-h-0 flex-col gap-2 overflow-hidden"
              : "bg-accent flex h-full min-h-0 flex-col gap-2 overflow-hidden rounded-md p-4"
          }
        >
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex h-10 w-full max-w-[350px] items-center">
              <Input
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value);
                }}
                placeholder="Search notes or author"
              />
            </div>
            {filterOptions.length > 0 && (
              <div
                onFocusCapture={() => {
                  if (massTortId) setDiscoverFilterOptions(true);
                }}
              >
                <FilterBar
                  filterOptions={filterOptions}
                  selectedFilters={selectedFilters}
                  onAddFilter={handleAddFilter}
                  onRemoveFilter={handleRemoveFilter}
                />
              </div>
            )}
          </div>
          {hasCreatePermission &&
            (variant === "default" || showComposerOverride) && (
              <MatterNoteComposer
                matter={matter}
                massTortId={massTortId}
                subItem={subItem}
                onCreateSuccess={onComposerSuccess}
                defaultCreatePinned={defaultCreatePinned}
                selectedDraft={selectedDraft}
                onDraftProcessed={() => {
                  setSelectedDraft(null);
                }}
              />
            )}
          {isError && (
            <div
              role="alert"
              className="flex items-center justify-between gap-2 text-sm"
            >
              <Text>Unable to load notes.</Text>
              <Button
                variant="outline"
                disabled={isFetching}
                onClick={() => {
                  void (notesQuery.isFetchNextPageError
                    ? fetchNextPage()
                    : refetch());
                }}
              >
                Retry
              </Button>
            </div>
          )}
          <AnimatePresence mode="wait">
            {isLoadingNotes ? (
              <div className="flex flex-1 items-center justify-center py-8">
                <LoadingSpinner className="h-6 w-6" />
              </div>
            ) : hasVisibleNotes || (hasNextPage && !isError) ? (
              <motion.div
                ref={attachScrollContainer}
                key="eventlog"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{
                  duration: showAnimation ? 0.3 : 0,
                  ease: "easeInOut",
                }}
                className="mt-2 flex flex-1 flex-col gap-2 overflow-auto pr-2"
              >
                {draftNotes.length > 0 && (
                  <Accordion type="single" collapsible>
                    <AccordionItem value="drafts">
                      <AccordionTrigger>
                        <AccordionTitle
                          title="Drafts"
                          count={draftNotes.length}
                        />
                      </AccordionTrigger>
                      <AccordionContent className="px-1">
                        <div className="flex flex-col gap-2">
                          {draftNotes.map((note) => {
                            return (
                              <div
                                key={note.id}
                                onClick={(event) => {
                                  const target =
                                    event.target as HTMLElement | null;
                                  if (
                                    target?.closest(
                                      "button, a, input, textarea, [role='button']",
                                    )
                                  ) {
                                    return;
                                  }
                                  setSelectedDraft(note);
                                }}
                              >
                                <MatterNote
                                  note={note}
                                  matter={matter}
                                  massTortId={massTortId}
                                  disableBreadcrumbs={disableBreadcrumbs}
                                  variant={variant}
                                  showComments={showComments}
                                  commentsMode={commentsMode}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  </Accordion>
                )}
                {showPinnedHighlight && (
                  <div className="flex flex-col gap-2">
                    <Text
                      variant="caption"
                      color="muted"
                      className="font-semibold tracking-wider uppercase"
                    >
                      Pinned notes ({pinnedNotes.length})
                    </Text>
                    <div className="flex flex-col gap-2">
                      {pinnedNotes.map((note) => (
                        <MatterNote
                          key={`pinned-${note.id}`}
                          note={note}
                          matter={matter}
                          massTortId={massTortId}
                          disableBreadcrumbs={disableBreadcrumbs}
                          variant={variant}
                          showComments={showComments}
                          commentsMode={commentsMode}
                          highlightPinned
                        />
                      ))}
                    </div>
                    <div className="my-1 flex items-center gap-2">
                      <Text
                        variant="caption"
                        color="muted"
                        className="font-semibold tracking-wider uppercase"
                      >
                        All notes ({totalNotesCount})
                      </Text>
                      <div className="border-border/70 flex-1 border-t" />
                    </div>
                  </div>
                )}
                {visibleRows.map((row) => (
                  <MatterNote
                    key={row.original.id}
                    note={row.original}
                    matter={matter}
                    massTortId={massTortId}
                    disableBreadcrumbs={disableBreadcrumbs}
                    variant={variant}
                    showComments={showComments}
                    commentsMode={commentsMode}
                    highlightPinned={!showPinnedHighlight}
                  />
                ))}
                {hasMoreRows && <div ref={sentinelRef} className="h-1" />}
                {hasNextPage && (
                  <div ref={infiniteScrollSentinelRef} className="h-1" />
                )}
                {isFetchingNextPage && (
                  <div className="text-muted-foreground flex items-center justify-center gap-2 py-3 text-sm">
                    <LoadingSpinner className="h-4 w-4" />
                    Loading more notes…
                  </div>
                )}
              </motion.div>
            ) : !isError ? (
              <Text variant="subHeading" className="mt-2">
                No notes found.
              </Text>
            ) : null}
          </AnimatePresence>
        </div>
      )}
    </>
  );
}
